/**
 * The service pop-out — the catalog's service, in the builder's own box
 * (`builder-engine-and-consistency.md` §6, board cb-04; phone cb-18 · 5 and
 * cb-22).
 *
 * **Still a pop-out, and empty by default.** Most services are a name, a
 * division and a measure; the recipe is optional and sits *below* the rest,
 * as a dashed panel until somebody adds a line. With lines, each is the
 * three-line `RecipeRow` (base + rate, the resolved quantity, the locked cost
 * chip), its ⋯ opens the same Line popover the quote uses, and every price,
 * cost and margin on the sheet is the **server's** — `POST
 * /catalog-admin/services/preview` projects the draft through `projectRecipe`,
 * the function a quote's expansion runs, at the `Try it at` measure.
 *
 * Props only, and saved by its own Save: this edits the catalog, so it is not
 * live-save like the quote.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { QUOTE_LINE_KIND_LABELS, type LineHandling, type OrgUnitSummary } from '@rowted/shared'
import { request } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { formatNumber, formatRate } from '../../lib/format.js'
import { usePhone } from '../../lib/use-narrow.js'
import { Band, Button, Switch } from '../../components/primitives.js'
import { FieldShell } from '../../components/fields.js'
import { BranchScopeField } from '../../components/form/branch-scope.js'
import { CheckRow } from '../../components/form/rows.js'
import { MenuSelect } from '../../components/menu-select.js'
import { IconLock } from '../../frame/icons.js'
import type { Division, MeasurementType, ServiceDetail, ServiceLine, ServiceLineKind } from '../../lib/catalog-types.js'
import { AddSearch, type Pickable, type PickKind } from '../../quote/quote-editors.js'
import { EditorInput } from '../editor-shell.js'
import { Sheet } from '../../components/sheet.js'
import type { TileKind } from '../kind-tile.js'
import { AddRow } from '../rows/add-row.js'
import { ADD_LINK_LABELS } from '../rows/line-row.js'
import { RecipeRow } from '../rows/recipe-row.js'
import { MeasureField } from '../quantity/measure-field.js'
import { UnitInput } from '../quantity/unit-input.js'
import { bandsOfDraft, draftTiersOf, readNumber } from '../quantity/tiers.js'
import { RecipeLinePopover, type RecipeLineDraft, type RecipeLineFigures } from './line-popover.js'
import { NotesField } from '../fields/notes-field.js'
import {
  ACTIVE, CATALOG_SERVICE_NOTE_HINTS, DIVISION_LABEL, DIVISION_NEEDED, MEASURED_BY_HINT, MEASURED_BY_HINT_PHONE,
  MEASURED_BY_LABEL, WHAT_IT_IS,
  MINIMUM_CHARGE, MINIMUM_CHARGE_HINT, NAME_LABEL, NAME_NEEDED, NEW_SERVICE, NOTHING_ADDED, NOTHING_ADDED_PHONE,
  OPTIONS_SECTION, PICK_A_DIVISION, RECIPE_LINE_FACTS, RESOLVES_TO, SAVE, SERVICE_SUB, STATUS,
  STRIP_COST, STRIP_MARGIN, STRIP_PRICE, TAXABLE_LABEL, TAXABLE_SERVICE_SUB, TRY_IT_AT,
  AT_MEASURE, WHATS_IN_IT, WHATS_IN_IT_EMPTY_SUB, WHATS_IN_IT_SUB, WHERE_OFFERED, WHERE_OFFERED_HINT,
} from '../copy.js'

/** The detail as `GET /catalog-admin/services/:id` returns it since step 9. */
type LoadedLine = ServiceLine & {
  optional?: boolean
  showOnProposal?: boolean
  taxable?: boolean | null
  priceOverride?: string | null
  catalogTaxable?: boolean
  handling?: LineHandling | null
  /** The line's own customer-facing name, and the catalog record's it follows (0126). */
  customerName?: string | null
  catalogCustomerName?: string | null
}
type Loaded = ServiceDetail & {
  taxable?: boolean
  /** The office's and the crew's notes beside `description`, the customer's (0125). */
  noteInternal?: string
  noteCrew?: string
  lines: LoadedLine[]
}

/** One recipe line as the sheet holds it: what it names, and the rule as typed. */
interface Line extends RecipeLineDraft {
  key: string
  kind: ServiceLineKind
  ids: Pick<ServiceLine, 'laborRateId' | 'itemId' | 'equipmentId' | 'rentalEquipmentId' | 'deploymentCostId'>
  note: string | null
  name: string
  unit: string | null
  cost: number | null
  price: number | null
  catalogTaxable: boolean
  /** What a blank Customer-facing name follows; null is the record's name. */
  catalogCustomerName: string | null
}

interface Preview {
  lines: { qty: number; unit: string; rate: string; amount: string; cost: string; marginPct: number | null; markupPct: number | null }[]
  total: { amount: string; cost: string; marginPct: number | null }
  targetMarginPct: number | null
}

/**
 * The four kinds a recipe offers — the quote's own `ADD_LINK_LABELS`, so the
 * words are one list. `rental` is a kind a line can still *be* (old rows keep
 * working) but there is no catalog behind it in `/catalog/pick`.
 */
const RECIPE_KINDS = ['item', 'labor', 'equipment', 'deployment'] as const

/** Above every popover the sheet raises: a line, the picker. */
const OVER = 60

/** "0.200000" → "0.2": a field somebody edits, not a figure to read. */
const trim = (value: string | number | null | undefined): string => {
  const text = String(value ?? '0')
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text
}

let keys = 0
const nextKey = () => `line-${++keys}`

function lineOf(loaded: LoadedLine): Line {
  const baseQty = Number(loaded.baseQty) || 0
  const qtyPerUnit = Number(loaded.qtyPerUnit) || 0
  const bands = loaded.bands.map((band) => ({
    minMeasure: Number(band.minMeasure) || 0,
    baseQty: Number(band.baseQty) || 0,
    qtyPerUnit: Number(band.qtyPerUnit) || 0,
  }))
  const { tiers, stacked } = draftTiersOf({ baseQty, qtyPerUnit, bands })
  return {
    key: loaded.id ?? nextKey(),
    kind: loaded.kind,
    ids: {
      laborRateId: loaded.laborRateId ?? null,
      itemId: loaded.itemId ?? null,
      equipmentId: loaded.equipmentId ?? null,
      rentalEquipmentId: loaded.rentalEquipmentId ?? null,
      deploymentCostId: loaded.deploymentCostId ?? null,
    },
    note: loaded.note ?? null,
    name: loaded.name ?? '',
    unit: loaded.unit ?? null,
    cost: loaded.cost === undefined || loaded.cost === null ? null : Number(loaded.cost),
    price: loaded.price === undefined || loaded.price === null ? null : Number(loaded.price),
    catalogTaxable: loaded.catalogTaxable ?? false,
    catalogCustomerName: loaded.catalogCustomerName ?? null,
    customerName: loaded.customerName ?? null,
    base: trim(loaded.baseQty),
    rate: trim(loaded.qtyPerUnit),
    tiers,
    legacyBands: stacked ? null : loaded.bands.map((band) => ({
      minMeasure: trim(band.minMeasure), baseQty: trim(band.baseQty), qtyPerUnit: trim(band.qtyPerUnit),
    })),
    optional: loaded.optional ?? false,
    showOnProposal: loaded.showOnProposal ?? true,
    taxable: loaded.taxable ?? null,
    priceOverride: loaded.priceOverride === undefined || loaded.priceOverride === null
      ? null : Number(loaded.priceOverride),
    handling: loaded.handling ?? null,
  }
}

/** What the preview and the save take for one line. */
function bodyOf(line: Line, draft: RecipeLineDraft = line) {
  return {
    kind: line.kind,
    ...line.ids,
    baseQty: readNumber(draft.base),
    qtyPerUnit: readNumber(draft.rate),
    bands: bandsOfDraft(draft),
    note: line.note,
    optional: draft.optional,
    showOnProposal: draft.showOnProposal,
    taxable: draft.taxable,
    priceOverride: draft.priceOverride,
    handling: line.kind === 'item' ? draft.handling : null,
    customerName: draft.customerName,
  }
}

const tileOf = (kind: ServiceLineKind): TileKind => (kind === 'rental' ? 'equipment' : kind)

/**
 * A crew hour is *counted* in `h` and *priced* per `crew h` (boards cb-04 and
 * cb-19); the catalog's labor unit is `crew hr`. Every other unit is the
 * catalog's own.
 */
const countUnit = (line: Line) => (line.kind === 'labor' ? 'h' : (line.unit ?? ''))
const priceUnit = (line: Line) => (line.kind === 'labor' ? 'crew h' : (line.unit ?? ''))

/** The count of tiers on a row's chip: stacked tiers, or the old bands plus the line's own. */
const tierCount = (line: Line) =>
  line.legacyBands ? line.legacyBands.length + 1 : line.tiers && line.tiers.length > 1 ? line.tiers.length : 0

export function ServicePopout({
  serviceId, divisions, measurementTypes, available, onClose, onSaved,
}: {
  serviceId: string | null
  divisions: Division[]
  measurementTypes: MeasurementType[]
  /** `me.scope.available`, exactly as the header's switcher gets it. */
  available: OrgUnitSummary[]
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const phone = usePhone()
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [name, setName] = useState('')
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [typeId, setTypeId] = useState<string | null>(null)
  const [minCharge, setMinCharge] = useState('0.00')
  const [active, setActive] = useState(true)
  const [taxable, setTaxable] = useState(false)
  /* The three notes: Customer is the catalog's `description`, which the proposal prints. */
  const [description, setDescription] = useState('')
  const [noteInternal, setNoteInternal] = useState('')
  const [noteCrew, setNoteCrew] = useState('')
  const [orgUnitIds, setOrgUnitIds] = useState<string[]>([])
  const [lines, setLines] = useState<Line[]>([])
  const [sample, setSample] = useState('1,000')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [errors, setErrors] = useState<{ name?: string; divisionId?: string }>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState<{ kind: PickKind; at: HTMLElement } | null>(null)
  const [editing, setEditing] = useState<{ key: string; at: HTMLElement | null } | null>(null)

  const ready = serviceId === null || loaded !== null

  useEffect(() => {
    if (!serviceId) return
    void request<Loaded>(`/catalog-admin/services/${serviceId}`)
      .then((detail) => {
        setLoaded(detail)
        setName(detail.name)
        setDivisionId(detail.divisionId)
        setTypeId(detail.measurementTypeId)
        setMinCharge(Number(detail.minCharge || 0).toFixed(2))
        setActive(detail.active)
        setTaxable(detail.taxable ?? false)
        setDescription(detail.description)
        setNoteInternal(detail.noteInternal ?? '')
        setNoteCrew(detail.noteCrew ?? '')
        setOrgUnitIds(detail.orgUnitIds)
        setLines(detail.lines.map(lineOf))
      })
      .catch((cause: Error) => setError(cause.message))
  }, [serviceId])

  const types = useMemo(() => measurementTypes.filter((type) => type.active || type.id === typeId), [measurementTypes, typeId])
  const type = types.find((one) => one.id === typeId) ?? null
  const measureUnit = type?.unit ?? null
  const measure = measureUnit ? readNumber(sample) : 0
  const division = divisions.find((one) => one.id === divisionId) ?? null

  /*
   * Every figure on the sheet, re-asked a quarter-second after the typing
   * stops. A stale answer is dropped rather than drawn over a newer one.
   */
  const asked = useRef(0)
  useEffect(() => {
    if (lines.length === 0) { setPreview(null); return }
    const ask = ++asked.current
    const timer = window.setTimeout(() => {
      void request<Preview>('/catalog-admin/services/preview', {
        method: 'POST',
        body: { measure, lines: lines.map((line) => bodyOf(line)) },
      }).then((answer) => { if (ask === asked.current) setPreview(answer) })
        .catch(() => { if (ask === asked.current) setPreview(null) })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [lines, measure])

  const setLine = (key: string, patch: Partial<Line>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)))

  const previewOne = (line: Line) => async (draft: RecipeLineDraft): Promise<RecipeLineFigures | null> => {
    try {
      const answer = await request<Preview>('/catalog-admin/services/preview', {
        method: 'POST', body: { measure, lines: [bodyOf(line, draft)] },
      })
      const one = answer.lines[0]
      return one ? { amount: one.amount, marginPct: one.marginPct, markupPct: one.markupPct } : null
    } catch {
      return null
    }
  }

  function add(pick: Pickable) {
    const body = pick.body as Record<string, string | undefined>
    /*
     * A new line on a measured service starts as one per measure; on one with
     * no measure, as one flat — what somebody dropping a bag of mulch into a
     * recipe means. Its unit, cost and price come back from the preview.
     */
    setLines((current) => [...current, {
      key: nextKey(),
      /*
       * A row of the equipment catalog is a `rental` line — owned kind or hired
       * in (0129) — because the kind is what says which column the id is in;
       * stored as `equipment` it named no machine and the database refused it.
       */
      kind: pick.kind === 'equipment' && body.rentalEquipmentId ? 'rental'
        : pick.kind === 'labor' || pick.kind === 'equipment' || pick.kind === 'deployment'
          ? pick.kind : 'item',
      ids: {
        laborRateId: body.laborRateId ?? null,
        itemId: body.itemId ?? null,
        equipmentId: body.equipmentId ?? null,
        rentalEquipmentId: body.rentalEquipmentId ?? null,
        deploymentCostId: body.deploymentCostId ?? null,
      },
      note: null,
      name: pick.label,
      unit: null,
      cost: null,
      price: null,
      catalogTaxable: false,
      catalogCustomerName: null,
      customerName: null,
      base: measureUnit ? '0' : '1',
      rate: measureUnit ? '1' : '0',
      tiers: null,
      legacyBands: null,
      optional: false,
      showOnProposal: true,
      taxable: null,
      priceOverride: null,
      handling: null,
    }])
    setAdding(null)
  }

  async function save() {
    const found: typeof errors = {}
    if (!name.trim()) found.name = NAME_NEEDED
    if (!divisionId) found.divisionId = DIVISION_NEEDED
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setError(null)
    try {
      const body = {
        name: name.trim(),
        description,
        noteInternal,
        noteCrew,
        divisionId,
        measurementTypeId: typeId,
        minCharge: Number(minCharge) || 0,
        orgUnitIds,
        active,
        taxable,
        lines: lines.map((line) => bodyOf(line)),
        ...(serviceId && loaded ? { version: loaded.version } : {}),
      }
      await request(serviceId ? `/catalog-admin/services/${serviceId}` : '/catalog-admin/services',
        { method: serviceId ? 'PUT' : 'POST', body })
      onSaved(serviceId ? `Saved ${body.name}.` : `Added ${body.name}.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const target = preview?.targetMarginPct ?? null
  const under = (pct: number | null | undefined) => pct !== null && pct !== undefined && target !== null && pct < target
  const open = editing ? lines.find((line) => line.key === editing.key) ?? null : null
  const openAt = editing ? lines.findIndex((line) => line.key === editing.key) : -1

  const addRow = (
    <AddRow
      look="buttons"
      grid={phone}
      kinds={RECIPE_KINDS.map((kind) => ({ key: kind, label: ADD_LINK_LABELS[kind] }))}
      onAdd={(key, at) => setAdding({ kind: key as PickKind, at })}
    />
  )

  const measureField = (
    <MeasureField value={typeId} onChange={setTypeId} serviceName={name || NEW_SERVICE}
      types={types.map((one) => ({ id: one.id, name: one.name, unit: one.unit }))} />
  )

  const tryItAt = measureUnit && (
    <span className={cn('inline-flex items-center gap-[7px] text-[12px] text-text-muted', !phone && 'ml-auto')}>
      {TRY_IT_AT}
      <UnitInput label={TRY_IT_AT} value={sample} onChange={setSample} size="small" width={70} align="right" />
      {measureUnit}
    </span>
  )

  /*
   * The product's record flyout, like every other catalog form (Aaron, step
   * 21 item 1) — it was a 480 box in the middle of the screen.
   */
  return (
    <Sheet
      open
      onClose={onClose}
      kind="service"
      title={name || NEW_SERVICE}
      sub={SERVICE_SUB(division?.name ?? null)}
      saveLabel={SAVE}
      onSave={() => { if (!busy && ready) void save() }}
      primary={(
        <Button variant="primary" className="h-[34px]" disabled={busy || !ready} onClick={() => void save()}>
          {busy ? 'Saving…' : SAVE}
        </Button>
      )}
    >
      <div data-service-popout="" className="flex flex-col gap-3.5">
        {error && <Band tone="amber">{error}</Band>}

        {/* What it is */}
        <div className="flex flex-col gap-3">
          <b className="pb-0.5 text-[14px] font-bold text-text">{WHAT_IT_IS}</b>
          <div className={cn('grid gap-x-3.5 gap-y-3', phone ? 'grid-cols-1' : 'grid-cols-2')}>
            <FieldShell label={NAME_LABEL} required className={phone ? undefined : 'col-span-2'}
              hint={errors.name && <span className="text-danger">{errors.name}</span>}>
              <EditorInput aria-label={NAME_LABEL} value={name}
                onChange={(event) => { setName(event.target.value); setErrors((was) => ({ ...was, name: undefined })) }} />
            </FieldShell>
            <FieldShell label={DIVISION_LABEL}
              hint={errors.divisionId && <span className="text-danger">{errors.divisionId}</span>}>
              <MenuSelect
                size="field"
                label={DIVISION_LABEL}
                value={divisionId}
                placeholder={PICK_A_DIVISION}
                onChange={(value) => { setDivisionId(value); setErrors((was) => ({ ...was, divisionId: undefined })) }}
                options={divisions.map((one) => ({ value: one.id, label: one.name }))}
              />
            </FieldShell>
            {phone
              ? (
                /* cb-22 · 2: the row carries its own label and opens a sheet. */
                <div className="flex flex-col gap-1">
                  {measureField}
                  <span className="text-[11px] leading-[1.4] text-text-faint">{MEASURED_BY_HINT_PHONE}</span>
                </div>
              )
              : <FieldShell label={MEASURED_BY_LABEL} hint={MEASURED_BY_HINT}>{measureField}</FieldShell>}
            <FieldShell label={MINIMUM_CHARGE} hint={MINIMUM_CHARGE_HINT}>
              <label className="flex h-9 items-center gap-1.5 rounded-control border border-line bg-surface px-[11px] focus-within:border-accent-line focus-within:ring-2 focus-within:ring-accent-ring">
                <span className="text-[13.5px] text-text-muted">$</span>
                <input aria-label={MINIMUM_CHARGE} inputMode="decimal" value={minCharge}
                  onChange={(event) => setMinCharge(event.target.value)}
                  onBlur={() => setMinCharge((Number(minCharge) || 0).toFixed(2))}
                  className="tnum min-w-0 flex-1 border-none bg-transparent p-0 text-[13.5px] text-text focus:outline-none focus-visible:shadow-none" />
              </label>
            </FieldShell>
            <FieldShell label={STATUS}>
              <span className="flex h-9 items-center">
                <Switch checked={active} onChange={setActive} label={ACTIVE} />
              </span>
            </FieldShell>
          </div>
        </div>

        {/* What's in it */}
        {lines.length === 0
          ? (
            <>
              <div className="flex items-baseline gap-2 pb-0.5 pt-2">
                <b className="text-[14px] font-bold text-text">{WHATS_IN_IT}</b>
                {/* cb-18 · 5: on a phone the sentence in the box says it, so the title stands alone. */}
                {!phone && <span className="text-[12px] leading-[1.35] text-text-muted">{WHATS_IN_IT_EMPTY_SUB}</span>}
              </div>
              <div data-recipe-empty="" className={cn('flex flex-col rounded-[12px] border border-dashed border-line-strong bg-surface-alt px-3.5',
                phone ? 'gap-2.5 py-3' : 'gap-3 pb-3 pt-3.5')}>
                <p className="m-0 text-[12.5px] leading-[1.5] text-text-muted">{phone ? NOTHING_ADDED_PHONE : NOTHING_ADDED}</p>
                {addRow}
              </div>
            </>
          )
          : (
            <div className="flex flex-col">
              <div className={cn('flex gap-2 pb-0.5 pt-2', phone ? 'flex-col items-start' : 'items-center')}>
                <span className="flex items-baseline gap-2">
                  <b className="text-[14px] font-bold text-text">{WHATS_IN_IT}</b>
                  <span className="text-[12px] text-text-muted">{WHATS_IN_IT_SUB(measureUnit)}</span>
                </span>
                {tryItAt}
              </div>
              <div className="mt-0.5">
                {lines.map((line, index) => {
                  const figures = preview?.lines[index]
                  const unit = countUnit(line) || figures?.unit || ''
                  const once = readNumber(line.rate) === 0
                  return (
                    <RecipeRow
                      key={line.key}
                      kind={tileOf(line.kind)}
                      name={line.name}
                      optional={line.optional}
                      total={figures ? formatRate(Number(figures.amount)) : null}
                      base={line.base}
                      rate={line.rate}
                      onBase={(base) => setLine(line.key, { base })}
                      onRate={(rate) => setLine(line.key, {
                        rate,
                        // The first tier's rate *is* the line's rate (§6.8 item 3).
                        tiers: line.tiers ? line.tiers.map((tier, at) => (at === 0 ? { ...tier, qtyPerUnit: rate } : tier)) : null,
                      })}
                      qtyUnit={unit}
                      measureUnit={measureUnit}
                      resolves={once || !figures || !measureUnit
                        ? null
                        : RESOLVES_TO(formatNumber(figures.qty), unit, formatNumber(measure), measureUnit)}
                      tierCount={measureUnit ? tierCount(line) : 0}
                      onTiers={(event) => setEditing({ key: line.key, at: event.currentTarget })}
                      cost={figures ? formatRate(Number(figures.cost)) : null}
                      marginPct={figures?.marginPct ?? null}
                      underTarget={under(figures?.marginPct)}
                      onMenu={(event) => setEditing({ key: line.key, at: event.currentTarget })}
                    />
                  )
                })}
              </div>
              {/*
                * The same hairline the quote's service draws between its last
                * line and the add links (step 22 item 3.1, carried here in
                * step 23), so the recipe and the quote read as one engine.
                */}
              <div data-add-row-rule="" className="border-t border-line-soft pb-2.5 pt-2.5">{addRow}</div>
              {preview && (
                <div data-recipe-totals="" className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-1 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-2.5 text-[12px] text-text-muted">
                  <span className="text-text-faint"><IconLock size={12} /></span>
                  {measureUnit && <span>{AT_MEASURE(formatNumber(measure), measureUnit)}</span>}
                  <span>{STRIP_PRICE} <b className="tnum text-text">{formatRate(Number(preview.total.amount))}</b></span>
                  <span>{STRIP_COST} <b className="tnum text-text">{formatRate(Number(preview.total.cost))}</b></span>
                  {preview.total.marginPct !== null && (
                    <span>
                      {STRIP_MARGIN}{' '}
                      <b className={cn('tnum', under(preview.total.marginPct) ? 'text-warn' : 'text-accent-dark')}>
                        {preview.total.marginPct.toFixed(1)}%
                      </b>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

        {/* Where it is offered · Options */}
        <div className={cn('grid gap-x-3.5 gap-y-3', phone ? 'grid-cols-1' : 'grid-cols-2')}>
          <FieldShell label={WHERE_OFFERED} hint={WHERE_OFFERED_HINT}>
            <BranchScopeField available={available} value={orgUnitIds} onChange={setOrgUnitIds} layer={OVER} />
          </FieldShell>
          <FieldShell label={OPTIONS_SECTION}>
            <CheckRow label={TAXABLE_LABEL} sub={TAXABLE_SERVICE_SUB} checked={taxable} onChange={setTaxable} />
          </FieldShell>
        </div>

        {/*
          * **Notes** (Aaron's review item 3): the visit popover's Customer ·
          * Internal · Crew block, unruled because the sheet's sections are
          * not, and last, where every other catalog form keeps its Notes.
          * Each quote this service is added to copies all three, and the
          * proposal prints the quote's Customer note under the service.
          */}
        <NotesField
          quote={description}
          internal={noteInternal}
          crew={noteCrew}
          onQuote={setDescription}
          onInternal={setNoteInternal}
          onCrew={setNoteCrew}
          hints={CATALOG_SERVICE_NOTE_HINTS}
          ruled={false}
        />
      </div>

      {open && (
        <RecipeLinePopover
          key={open.key}
          name={open.name}
          kind={tileOf(open.kind)}
          facts={RECIPE_LINE_FACTS(QUOTE_LINE_KIND_LABELS[open.kind === 'rental' ? 'equipment' : open.kind], name || NEW_SERVICE)}
          qtyUnit={countUnit(open) || preview?.lines[openAt]?.unit || ''}
          priceUnit={priceUnit(open) || preview?.lines[openAt]?.unit || ''}
          measureUnit={measureUnit}
          typeName={type?.name ?? ''}
          sample={measure}
          unitCost={open.cost ?? unitCostOf(preview?.lines[openAt])}
          standard={open.price ?? Number(preview?.lines[openAt]?.rate ?? 0)}
          draft={open}
          catalogTaxable={open.catalogTaxable}
          catalogCustomerName={open.catalogCustomerName}
          onPreview={previewOne(open)}
          onDone={(draft) => setLine(open.key, draft)}
          onRemove={() => setLines((current) => current.filter((line) => line.key !== open.key))}
          onClose={() => setEditing(null)}
          anchor={editing?.at ?? null}
          layer={OVER}
        />
      )}

      <AddSearch
        open={adding !== null}
        anchor={adding?.at ?? null}
        mode="line"
        startKind={adding?.kind}
        onClose={() => setAdding(null)}
        onPick={(pick) => add(pick)}
        layer={OVER}
      />
    </Sheet>
  )
}

/** A line nobody has saved yet carries no cost per unit; the preview's total over its count is it. */
function unitCostOf(line: Preview['lines'][number] | undefined): number {
  if (!line || !line.qty) return 0
  return Math.round((Number(line.cost) / line.qty) * 10000) / 10000
}

